import { useEffect, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface PortalCompanyOption {
  id: string;
  name: string;
}

const STORAGE_KEY = "client-portal-company-id";

let portalCompanyIdCache: string | null | undefined;

const listeners = new Set<() => void>();

const notifyPortalCompanyChange = () => {
  listeners.forEach((listener) => listener());
};

const readPortalCompanyId = () => {
  if (portalCompanyIdCache !== undefined) {
    return portalCompanyIdCache;
  }

  if (typeof window === "undefined") {
    return null;
  }

  portalCompanyIdCache = window.localStorage.getItem(STORAGE_KEY);
  return portalCompanyIdCache;
};

export const setStoredPortalCompanyId = (companyId: string | null) => {
  portalCompanyIdCache = companyId;

  if (typeof window !== "undefined") {
    if (companyId) {
      window.localStorage.setItem(STORAGE_KEY, companyId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }

  notifyPortalCompanyChange();
};

const subscribeToPortalCompany = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePortalCompany = () => {
  const { user, loading, companyId: authCompanyId, companyName: authCompanyName, isAdmin, isEmployee } = useAuth();
  const canSwitchPortalCompany = isAdmin || isEmployee;
  const selectedPortalCompanyId = useSyncExternalStore(subscribeToPortalCompany, readPortalCompanyId, () => null);

  const { data: portalCompanyOptions = [], isLoading } = useQuery({
    queryKey: ["portal-company-options", user?.id, isAdmin, isEmployee],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name")
        .order("name");

      if (error) throw error;
      return (data ?? []) as PortalCompanyOption[];
    },
    enabled: !loading && !!user && canSwitchPortalCompany,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!canSwitchPortalCompany) {
      if (selectedPortalCompanyId) {
        setStoredPortalCompanyId(null);
      }
      return;
    }

    if (portalCompanyOptions.length === 0) {
      return;
    }

    if (selectedPortalCompanyId && portalCompanyOptions.some((company) => company.id === selectedPortalCompanyId)) {
      return;
    }

    const fallbackCompany = portalCompanyOptions.find((company) => company.id !== authCompanyId) ?? portalCompanyOptions[0];
    setStoredPortalCompanyId(fallbackCompany?.id ?? null);
  }, [authCompanyId, canSwitchPortalCompany, portalCompanyOptions, selectedPortalCompanyId]);

  const activePortalCompany = canSwitchPortalCompany
    ? portalCompanyOptions.find((company) => company.id === selectedPortalCompanyId) ??
      portalCompanyOptions.find((company) => company.id === authCompanyId)
    : undefined;

  return {
    companyId: canSwitchPortalCompany ? activePortalCompany?.id ?? authCompanyId : authCompanyId,
    companyName: canSwitchPortalCompany ? activePortalCompany?.name ?? authCompanyName : authCompanyName,
    canSwitchPortalCompany,
    portalCompanyOptions,
    setPortalCompanyId: setStoredPortalCompanyId,
    loadingPortalCompany: canSwitchPortalCompany && isLoading && portalCompanyOptions.length === 0,
  };
};